import { useCallback, useMemo } from 'react';
import { microServicesRoutes } from '@constants';
import { contactsApi } from '@lib/apiClients';
import { logError } from '@lib/logger/logError';
import { extractErrorMessage } from '@lib/logger/extractErrorMessage';
import type { AxiosResponse } from 'axios';

export type VoicemailStatus = 'active' | 'draft' | 'default';

export interface VoicemailDto {
  id?: string;
  _id?: string;
  title: string;
  recordingUrl?: string;
  url?: string;
  durationSec?: number;
  status?: VoicemailStatus;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpdateVoicemailDto {
  id?: string;
  _id?: string;
  title?: string;
  status?: VoicemailStatus;
}

export interface ApiError { message: string; raw?: string }
export type ApiResponse<T> =
  | { success: true; data: T; totalPages?: number; totalCount?: number; pageNumber?: number }
  | { success: false; error: ApiError };


export interface VoicemailApiCompat extends VoicemailDto {
  id?: string;
  _id?: string;

  recordingUrl?: string;
  url?: string;

  duration?: number | string;
  recordingDuration?: number | string;
  duration_seconds?: number | string;
  length?: number | string;

  createdAt?: string;
  updatedAt?: string;

  status?: VoicemailStatus;
}

export default function useVoicemailApi (): {
  getVoicemailsBySdrIdAsync: (userId: string) => Promise<ApiResponse<VoicemailDto[]>>;
  getVoicemailByIdAsync: (id: string) => Promise<ApiResponse<VoicemailDto>>;
  getVoicemailByCallSidAsync: (callSid: string) => Promise<ApiResponse<VoicemailDto>>;
  updateVoicemailAsync: (dto: UpdateVoicemailDto) => Promise<ApiResponse<VoicemailDto>>;
  deleteVoicemailAsync: (id: string) => Promise<ApiResponse<null>>;
  } {
  const handleError = useCallback(<T = never>(
    message: string,
    error: unknown,
    source: string,
  ): ApiResponse<T> => {
    const errorMessage = extractErrorMessage(error);
    void logError(message, source, { error: errorMessage, rawError: error }, false);
    return { success: false, error: { message, raw: errorMessage } };
  }, []);

  const getVoicemailsBySdrIdAsync = useCallback(async (
    userId: string,
  ): Promise<ApiResponse<VoicemailDto[]>> => {
    try {
      const url = `${microServicesRoutes.contacts.get.recordings.getRecordingsBySdrIdNest}/${userId}`;
      const res: AxiosResponse<VoicemailDto[]> = await contactsApi.get(url);
      return { success: true, data: res.data ?? [] };
    } catch (e) {
      return handleError<VoicemailDto[]>(
        'Error fetching voicemails by userId.',
        e,
        'Voicemail.getVoicemailsBySdrIdAsync',
      );
    }
  }, [handleError]);

  const getVoicemailByIdAsync = useCallback(async (
    id: string,
  ): Promise<ApiResponse<VoicemailDto>> => {
    try {
      const url = `${microServicesRoutes.contacts.get.recordings.getRecordingById}/${id}`;
      const res = await contactsApi.get<VoicemailDto>(url);
      return { success: true, data: res.data };
    } catch (e) {
      return handleError<VoicemailDto>(
        'Error fetching voicemail by id.',
        e,
        'Voicemail.getVoicemailByIdAsync',
      );
    }
  }, [handleError]);

  const getVoicemailByCallSidAsync = useCallback(async (
    callSid: string,
  ): Promise<ApiResponse<VoicemailDto>> => {
    try {
      const url = `${microServicesRoutes.contacts.get.recordings.getRecordingByCallId}/${callSid}`;
      const res = await contactsApi.get<VoicemailDto>(url);
      return { success: true, data: res.data };
    } catch (e) {
      return handleError<VoicemailDto>(
        'Error fetching voicemail by CallSid.',
        e,
        'Voicemail.getVoicemailByCallSidAsync',
      );
    }
  }, [handleError]);

  const updateVoicemailAsync = useCallback(async (
    dto: UpdateVoicemailDto,
  ): Promise<ApiResponse<VoicemailDto>> => {
    try {
      const compatId = dto._id ?? dto.id ?? '';
      if (compatId === '') {
        return { success: false, error: { message: 'Voicemail id is required to update.' } };
      }

      const url = `${microServicesRoutes.contacts.put.voicemail.updateVoicemailNest}/${compatId}`;

      const body: Record<string, unknown> = {
        ...dto,
        id: compatId,
      };

      const res = await contactsApi.put<VoicemailDto>(url, body);
      return { success: true, data: res.data };
    } catch (e) {
      return handleError<VoicemailDto>(
        'Error trying to update voicemail.',
        e,
        'Voicemail.updateVoicemailAsync',
      );
    }
  }, [handleError]);

  const deleteVoicemailAsync = useCallback(async (
    id: string,
  ): Promise<ApiResponse<null>> => {
    try {
      const url = `${microServicesRoutes.contacts.delete.voicemail.deleteVoicemailNest}/${id}`;
      await contactsApi.delete(url);
      return { success: true, data: null };
    } catch (e) {
      return handleError<null>(
        'Error trying to delete voicemail.',
        e,
        'Voicemail.deleteVoicemailAsync',
      );
    }
  }, [handleError]);

  return useMemo(() => ({
    getVoicemailsBySdrIdAsync,
    getVoicemailByIdAsync,
    getVoicemailByCallSidAsync,
    updateVoicemailAsync,
    deleteVoicemailAsync,
  }), [
    getVoicemailsBySdrIdAsync,
    getVoicemailByIdAsync,
    getVoicemailByCallSidAsync,
    updateVoicemailAsync,
    deleteVoicemailAsync,
  ]);
}
